import { useEffect, useMemo, useState } from 'react';
import { Calendar, Clock, DollarSign, LogIn, LogOut, Pencil, Plus, Trash2, Users } from 'lucide-react';
import {
  colombiaDateString,
  colombiaHourMinute,
  hasPermission,
  isAfter2pmColombia,
  type NurseRates,
  type NurseShift,
  type PayrollLine,
} from '@datazo/contracts';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/shared/lib/cn';
import { usePageTitle } from '@/shared/store/page';
import { useAuthStore } from '@/shared/store/auth';
import { useToast } from '@/shared/toast/toast-context';
import {
  useClockIn,
  useClockOut,
  useCreateShift,
  useCurrentShift,
  useEditShift,
  useNurseRates,
  useDeleteShift,
  useNurseShifts,
  usePaystub,
  usePaystubAll,
} from './api';

/**
 * Página única de payroll que sirve a 2 audiencias:
 *  - Enfermera: ve su clock-in/out grande, su jornada actual y su liquidación.
 *  - Admin: ve todo lo de arriba PLUS un selector para liquidar a cualquier
 *    enfermera y la tabla de tarifas.
 *
 * Se renderizan secciones diferentes según el `rol` del usuario autenticado.
 */
export function PayrollPage(): JSX.Element {
  usePageTitle('Payroll', 'Asistencia, jornadas y liquidación quincenal de enfermería');
  const authUser = useAuthStore((s) => s.user);
  const isAdmin = authUser?.rol === 'admin';
  const isNurse = authUser?.rol === 'enfermera';

  // La cuenta compartida de enfermería opera el módulo para TODAS las enfermeras
  // del directorio (selecciona cuál marca entrada/salida y a quién liquidar),
  // igual que el admin. No hay logins individuales de enfermera.
  const canManageNurses = isAdmin || isNurse;

  return (
    <div className="flex flex-col gap-5">
      {/* Panel de marcación entrada/salida — elige enfermera del directorio */}
      {canManageNurses && <NurseTodayPanel />}

      {/* Total consolidado del periodo — solo admin */}
      {isAdmin && <PaystubAllSection />}

      {/* Calculadora quincenal — se elige la enfermera del directorio */}
      <PaystubSection isAdmin={canManageNurses} ownNurseId={undefined} />

      {/* Histórico de turnos */}
      <ShiftsHistorySection isAdmin={canManageNurses} ownNurseId={undefined} />

      {/* Tabla de tarifas */}
      {canManageNurses && <RatesTable />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Nurse today panel — clock-in/out + reloj                                    */
/* -------------------------------------------------------------------------- */

function NurseTodayPanel(): JSX.Element {
  const { showToast } = useToast();
  const ratesQuery = useNurseRates();
  const nurses = ratesQuery.data?.items ?? [];
  const [selectedNurseId, setSelectedNurseId] = useState<string | undefined>(undefined);

  // Auto-selección de la primera enfermera cuando llega el directorio.
  useEffect(() => {
    if (selectedNurseId === undefined && nurses.length > 0) {
      setSelectedNurseId(nurses[0]!.nurseId);
    }
  }, [nurses, selectedNurseId]);

  const { data, isLoading } = useCurrentShift(selectedNurseId);
  const clockIn = useClockIn();
  const clockOut = useClockOut();
  const [now, setNow] = useState<Date>(() => new Date());

  // Tick para el reloj del shift activo
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const handleClockIn = (): void => {
    if (selectedNurseId === undefined) return;
    clockIn.mutate(
      { nurseId: selectedNurseId },
      {
        onSuccess: () => showToast({ kind: 'success', title: 'Entrada registrada' }),
        onError: (err) =>
          showToast({ kind: 'danger', title: 'No se pudo registrar', description: err.message }),
      },
    );
  };
  const handleClockOut = (): void => {
    if (selectedNurseId === undefined) return;
    clockOut.mutate(
      { nurseId: selectedNurseId },
      {
        onSuccess: () => showToast({ kind: 'success', title: 'Salida registrada' }),
        onError: (err) =>
          showToast({ kind: 'danger', title: 'No se pudo registrar', description: err.message }),
      },
    );
  };

  const open = data?.shift !== null && data?.shift !== undefined;
  const elapsed = useMemo(() => {
    if (!open) return null;
    const start = new Date(data!.shift!.inicioAt);
    const diffMs = now.getTime() - start.getTime();
    if (diffMs < 0) return '00:00:00';
    const h = Math.floor(diffMs / 3_600_000);
    const m = Math.floor((diffMs % 3_600_000) / 60_000);
    const s = Math.floor((diffMs % 60_000) / 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }, [open, data, now]);

  const horaColombia = colombiaHourMinute(now);
  const fechaColombia = colombiaDateString(now);

  return (
    <Card
      className={cn(
        'p-6',
        open
          ? 'bg-gradient-to-br from-teal-50 to-sky-50 border-teal-300'
          : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300',
      )}
    >
      <div className="mb-4 flex flex-col gap-1 max-w-sm">
        <Label htmlFor="clock-nurse-select" className="text-[11px] uppercase tracking-wider text-text-muted">
          Enfermera
        </Label>
        <select
          id="clock-nurse-select"
          value={selectedNurseId ?? ''}
          onChange={(e) => setSelectedNurseId(e.target.value === '' ? undefined : e.target.value)}
          className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="">— Selecciona la enfermera —</option>
          {nurses.map((n) => (
            <option key={n.nurseId} value={n.nurseId}>
              {n.nurseNombre ?? n.nurseId}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-text-muted">
            Jornada de hoy · {fechaColombia} · Colombia
          </div>
          <div className="text-3xl font-extrabold text-text mt-1 tabular-nums">
            {open ? elapsed : 'Sin jornada abierta'}
          </div>
          <div className="text-sm text-text-muted mt-1">
            {open
              ? `Entrada: ${colombiaHourMinute(new Date(data!.shift!.inicioAt))} · Hora actual: ${horaColombia}`
              : `Hora actual Colombia: ${horaColombia}`}
          </div>
          {data !== undefined && (
            <div className="mt-3 flex items-center gap-2 flex-wrap text-sm">
              <Badge variant={data.procedimientosHoy > 20 ? 'green' : 'gray'}>
                <Users className="w-3 h-3" /> {data.procedimientosHoy} procedimiento
                {data.procedimientosHoy === 1 ? '' : 's'} hoy
              </Badge>
              {data.procedimientosHoy > 20 && (
                <Badge variant="green">+Bono &gt; 20 pacientes</Badge>
              )}
              {open && isAfter2pmColombia(now) && (
                <Badge variant="amber">+Bono hora extra (≥ 2:00 p.m.)</Badge>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 items-stretch min-w-[200px]">
          {open ? (
            <Button
              size="lg"
              variant="danger"
              onClick={handleClockOut}
              disabled={clockOut.isPending || isLoading || selectedNurseId === undefined}
            >
              <LogOut className="w-5 h-5" />
              {clockOut.isPending ? 'Registrando…' : 'Marcar salida'}
            </Button>
          ) : (
            <Button
              size="lg"
              onClick={handleClockIn}
              disabled={clockIn.isPending || isLoading || selectedNurseId === undefined}
            >
              <LogIn className="w-5 h-5" />
              {clockIn.isPending ? 'Registrando…' : 'Marcar entrada'}
            </Button>
          )}
          {!open && data !== undefined && (
            <p className="text-[11px] text-text-muted text-center">
              Solo puedes registrar una entrada por día.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Paystub section                                                            */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* PaystubAll section — total consolidado de la quincena (admin)              */
/* -------------------------------------------------------------------------- */

function PaystubAllSection(): JSX.Element {
  const today = new Date();
  const [year, setYear] = useState<number>(today.getFullYear());
  const [month, setMonth] = useState<number>(today.getMonth() + 1);
  const [quincena, setQuincena] = useState<1 | 2>(today.getDate() <= 15 ? 1 : 2);

  const totals = usePaystubAll({ year, month, quincena });

  return (
    <Card className="p-6">
      <CardHeader className="px-0 pt-0 pb-4 mb-4 border-b border-border">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Total quincena consolidado
            </CardTitle>
            <CardDescription>
              Suma de lo que se está pagando este periodo por todo el equipo de enfermería.
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <PeriodPicker
        year={year}
        month={month}
        quincena={quincena}
        onYear={setYear}
        onMonth={setMonth}
        onQuincena={setQuincena}
      />

      {totals.isLoading && (
        <div className="text-text-muted text-sm mt-4">Calculando totales del equipo…</div>
      )}
      {totals.isError && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {totals.error?.message ?? 'Error cargando el consolidado'}
        </div>
      )}
      {totals.data !== undefined && <PaystubAllView totals={totals.data} />}
    </Card>
  );
}

function PaystubAllView({
  totals,
}: {
  totals: import('@datazo/contracts').PayrollPeriodTotal;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Enfermeras con jornada"
          value={`${totals.totalEnfermerasConJornada} de ${totals.paystubs.length}`}
        />
        <StatTile label="Días-jornada acumulados" value={`${totals.totalDiasTrabajados}`} />
        <StatTile label="Jornada normal" value={formatCop(totals.totalJornadaNormal)} />
        <StatTile
          label="Bonos (>20 + extra)"
          value={formatCop(totals.totalAdicionalMas20 + totals.totalHoraExtra)}
        />
      </div>

      <div className="rounded-md border-2 border-primary bg-primary-soft p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-primary">
            Total a pagar · Quincena {totals.quincena} · {totals.periodoInicio} → {totals.periodoFin}
          </div>
          <div className="text-[11px] text-text-muted mt-0.5">
            Suma de los paystubs de todas las enfermeras del equipo.
          </div>
        </div>
        <div className="text-3xl font-extrabold text-primary tabular-nums">
          {formatCop(totals.totalPeriodo)}
        </div>
      </div>

      <div className="rounded-md border border-border overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-surface-alt text-[10px] uppercase tracking-wider text-text-muted">
            <tr>
              <th className="px-3 py-2 text-left">Enfermera</th>
              <th className="px-3 py-2 text-right">Días</th>
              <th className="px-3 py-2 text-right">Jornada</th>
              <th className="px-3 py-2 text-right">+20 pacs</th>
              <th className="px-3 py-2 text-right">Hora extra</th>
              <th className="px-3 py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {totals.paystubs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-text-muted">
                  No hay enfermeras con tarifas configuradas.
                </td>
              </tr>
            ) : (
              totals.paystubs.map((p) => (
                <tr key={p.nurseId} className="border-t border-border">
                  <td className="px-3 py-2 font-semibold">{p.nurseNombre}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.diasTrabajados}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatCop(p.totalJornadaNormal)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {p.totalAdicionalMas20 > 0 ? (
                      formatCop(p.totalAdicionalMas20)
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {p.totalHoraExtra > 0 ? (
                      formatCop(p.totalHoraExtra)
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-extrabold tabular-nums text-primary">
                    {formatCop(p.total)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot className="bg-surface-alt border-t-2 border-primary/40">
            <tr>
              <td className="px-3 py-2 font-bold text-text-soft uppercase text-[10px] tracking-wider">
                Total equipo
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-bold">
                {totals.totalDiasTrabajados}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-bold">
                {formatCop(totals.totalJornadaNormal)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-bold">
                {formatCop(totals.totalAdicionalMas20)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-bold">
                {formatCop(totals.totalHoraExtra)}
              </td>
              <td className="px-3 py-2 text-right font-extrabold tabular-nums text-primary text-base">
                {formatCop(totals.totalPeriodo)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* PeriodPicker — reutilizable para PaystubAll + PaystubSection                */
/* -------------------------------------------------------------------------- */

interface PeriodPickerProps {
  year: number;
  month: number;
  quincena: 1 | 2;
  onYear(y: number): void;
  onMonth(m: number): void;
  onQuincena(q: 1 | 2): void;
}

function PeriodPicker({
  year,
  month,
  quincena,
  onYear,
  onMonth,
  onQuincena,
}: PeriodPickerProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <Label className="text-[11px] uppercase tracking-wider text-text-muted">Año</Label>
        <input
          type="number"
          min={2000}
          max={2200}
          value={year}
          onChange={(e) => onYear(parseInt(e.target.value, 10))}
          className="h-10 w-24 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-[11px] uppercase tracking-wider text-text-muted">Mes</Label>
        <select
          value={month}
          onChange={(e) => onMonth(parseInt(e.target.value, 10))}
          className="h-10 w-32 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          {[
            'Enero',
            'Febrero',
            'Marzo',
            'Abril',
            'Mayo',
            'Junio',
            'Julio',
            'Agosto',
            'Septiembre',
            'Octubre',
            'Noviembre',
            'Diciembre',
          ].map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-[11px] uppercase tracking-wider text-text-muted">Quincena</Label>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => onQuincena(1)}
            className={cn(
              'h-10 px-4 rounded-md text-sm font-semibold border',
              quincena === 1
                ? 'bg-primary text-white border-primary'
                : 'bg-surface text-text border-border hover:border-primary',
            )}
          >
            1 (1–15)
          </button>
          <button
            type="button"
            onClick={() => onQuincena(2)}
            className={cn(
              'h-10 px-4 rounded-md text-sm font-semibold border',
              quincena === 2
                ? 'bg-primary text-white border-primary'
                : 'bg-surface text-text border-border hover:border-primary',
            )}
          >
            2 (16–fin)
          </button>
        </div>
      </div>
    </div>
  );
}

interface PaystubSectionProps {
  isAdmin: boolean;
  ownNurseId: string | undefined;
}

function PaystubSection({ isAdmin, ownNurseId }: PaystubSectionProps): JSX.Element {
  const today = new Date();
  const [year, setYear] = useState<number>(today.getFullYear());
  const [month, setMonth] = useState<number>(today.getMonth() + 1);
  const [quincena, setQuincena] = useState<1 | 2>(today.getDate() <= 15 ? 1 : 2);

  const ratesQuery = useNurseRates();
  const nurses = ratesQuery.data?.items ?? [];
  const [selectedNurseId, setSelectedNurseId] = useState<string | undefined>(ownNurseId);

  // Cuando llegan las tarifas y soy admin sin selección previa, escoger primera.
  useEffect(() => {
    if (selectedNurseId === undefined && isAdmin && nurses.length > 0) {
      setSelectedNurseId(nurses[0]!.nurseId);
    }
  }, [isAdmin, nurses, selectedNurseId]);

  const paystub = usePaystub({
    nurseId: selectedNurseId,
    year,
    month,
    quincena,
  });

  return (
    <Card className="p-6">
      <CardHeader className="px-0 pt-0 pb-4 mb-4 border-b border-border">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Liquidación quincenal
            </CardTitle>
            <CardDescription>
              Día 1–15 = Quincena 1 · Día 16–fin de mes = Quincena 2. Cálculo en COP.
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <div className="flex flex-wrap items-end gap-3 mb-5">
        {isAdmin && (
          <div className="flex flex-col gap-1 min-w-[220px]">
            <Label htmlFor="nurse-select" className="text-[11px] uppercase tracking-wider text-text-muted">
              Enfermera
            </Label>
            <select
              id="nurse-select"
              value={selectedNurseId ?? ''}
              onChange={(e) => setSelectedNurseId(e.target.value === '' ? undefined : e.target.value)}
              className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {nurses.map((n) => (
                <option key={n.nurseId} value={n.nurseId}>
                  {n.nurseNombre ?? n.nurseId}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <Label htmlFor="year-input" className="text-[11px] uppercase tracking-wider text-text-muted">
            Año
          </Label>
          <input
            id="year-input"
            type="number"
            min={2000}
            max={2200}
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="h-10 w-24 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="month-input" className="text-[11px] uppercase tracking-wider text-text-muted">
            Mes
          </Label>
          <select
            id="month-input"
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value, 10))}
            className="h-10 w-32 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {[
              'Enero',
              'Febrero',
              'Marzo',
              'Abril',
              'Mayo',
              'Junio',
              'Julio',
              'Agosto',
              'Septiembre',
              'Octubre',
              'Noviembre',
              'Diciembre',
            ].map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wider text-text-muted">Quincena</Label>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setQuincena(1)}
              className={cn(
                'h-10 px-4 rounded-md text-sm font-semibold border',
                quincena === 1
                  ? 'bg-primary text-white border-primary'
                  : 'bg-surface text-text border-border hover:border-primary',
              )}
            >
              1 (1–15)
            </button>
            <button
              type="button"
              onClick={() => setQuincena(2)}
              className={cn(
                'h-10 px-4 rounded-md text-sm font-semibold border',
                quincena === 2
                  ? 'bg-primary text-white border-primary'
                  : 'bg-surface text-text border-border hover:border-primary',
              )}
            >
              2 (16–fin)
            </button>
          </div>
        </div>
      </div>

      {paystub.isLoading && <div className="text-text-muted text-sm">Calculando…</div>}
      {paystub.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {paystub.error?.message ?? 'Error cargando la liquidación'}
        </div>
      )}
      {paystub.data !== undefined && <PaystubView paystub={paystub.data} />}
    </Card>
  );
}

function PaystubView({ paystub }: { paystub: import('@datazo/contracts').PayrollPaystub }): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Días trabajados" value={`${paystub.diasTrabajados}`} />
        <StatTile label="Jornada normal" value={formatCop(paystub.totalJornadaNormal)} />
        <StatTile label="Bono > 20 pacientes" value={formatCop(paystub.totalAdicionalMas20)} />
        <StatTile label="Bono hora extra" value={formatCop(paystub.totalHoraExtra)} />
      </div>

      <div className="rounded-md border-2 border-primary bg-primary-soft p-4 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider font-bold text-primary">
          Total a pagar · {paystub.periodoInicio} → {paystub.periodoFin}
        </div>
        <div className="text-3xl font-extrabold text-primary tabular-nums">
          {formatCop(paystub.total)}
        </div>
      </div>

      <div className="rounded-md border border-border overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-surface-alt text-[10px] uppercase tracking-wider text-text-muted">
            <tr>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Entrada</th>
              <th className="px-3 py-2 text-left">Salida</th>
              <th className="px-3 py-2 text-right">Procs.</th>
              <th className="px-3 py-2 text-center">Bonos</th>
              <th className="px-3 py-2 text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {paystub.lineas.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-text-muted">
                  Sin jornadas registradas en esta quincena.
                </td>
              </tr>
            ) : (
              paystub.lineas.map((l) => <PaystubLine key={l.fecha} line={l} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PaystubLine({ line }: { line: PayrollLine }): JSX.Element {
  return (
    <tr className="border-t border-border">
      <td className="px-3 py-2 font-mono text-xs">{line.fecha}</td>
      <td className="px-3 py-2 tabular-nums">{colombiaHourMinute(new Date(line.inicioAt))}</td>
      <td className="px-3 py-2 tabular-nums">
        {line.horaSalidaCol ?? <span className="text-text-muted">— en curso —</span>}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{line.procedimientos}</td>
      <td className="px-3 py-2 text-center">
        <div className="flex gap-1 justify-center flex-wrap">
          {line.aplicaMas20Pacientes && <Badge variant="green">&gt;20</Badge>}
          {line.aplicaHoraExtra && <Badge variant="amber">≥2pm</Badge>}
          {!line.aplicaMas20Pacientes && !line.aplicaHoraExtra && (
            <span className="text-text-muted text-xs">—</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-right font-bold tabular-nums">{formatCop(line.subtotal)}</td>
    </tr>
  );
}

function StatTile({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-surface px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">{label}</div>
      <div className="text-xl font-extrabold text-text mt-1 tabular-nums">{value}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Histórico de jornadas                                                       */
/* -------------------------------------------------------------------------- */

interface ShiftsHistoryProps {
  isAdmin: boolean;
  ownNurseId: string | undefined;
}

function ShiftsHistorySection({ isAdmin, ownNurseId }: ShiftsHistoryProps): JSX.Element {
  const today = colombiaDateString(new Date());
  const firstOfMonth = today.slice(0, 8) + '01';
  const [desde, setDesde] = useState<string>(firstOfMonth);
  const [hasta, setHasta] = useState<string>(today);
  const ratesQuery = useNurseRates();
  const nurses = ratesQuery.data?.items ?? [];
  const [nurseId, setNurseId] = useState<string | undefined>(ownNurseId);

  // Gestionar jornadas (registrar manual, editar, cerrar/reabrir, eliminar)
  // requiere `payroll:manage` (admin) — NO la cuenta de enfermería.
  const permisos = useAuthStore((s) => s.user?.permisos);
  const canManage = hasPermission(permisos ?? [], 'payroll:manage');
  const canDelete = canManage;
  const deleteShift = useDeleteShift();
  const { showToast } = useToast();
  const [editing, setEditing] = useState<
    { shift: NurseShift; nurse: string } | null
  >(null);
  const [toDelete, setToDelete] = useState<{ id: string; fecha: string; nurse: string } | null>(
    null,
  );
  const confirmDelete = async (): Promise<void> => {
    if (toDelete === null) return;
    try {
      await deleteShift.mutateAsync(toDelete.id);
      showToast({ kind: 'success', title: 'Jornada eliminada' });
      setToDelete(null);
    } catch (err) {
      showToast({
        kind: 'danger',
        title: 'No se pudo eliminar',
        description: err instanceof Error ? err.message : 'Error inesperado',
      });
    }
  };
  const colCount = 4 + (isAdmin ? 1 : 0) + (canDelete ? 1 : 0);

  const shifts = useNurseShifts({
    ...(isAdmin ? { ...(nurseId !== undefined ? { nurseId } : {}) } : {}),
    desde,
    hasta,
  });

  return (
    <>
    <Card className="p-6">
      <CardHeader className="px-0 pt-0 pb-4 mb-4 border-b border-border">
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary" />
          Histórico de jornadas
        </CardTitle>
        <CardDescription>
          {isAdmin
            ? 'Selecciona una enfermera y rango de fechas. Vacío = todas las enfermeras.'
            : 'Tus jornadas registradas en el rango seleccionado.'}
        </CardDescription>
      </CardHeader>

      {canManage && <ManualShiftForm nurses={nurses} />}

      <div className="flex flex-wrap items-end gap-3 mb-4">
        {isAdmin && (
          <div className="flex flex-col gap-1 min-w-[220px]">
            <Label className="text-[11px] uppercase tracking-wider text-text-muted">Enfermera</Label>
            <select
              value={nurseId ?? ''}
              onChange={(e) => setNurseId(e.target.value === '' ? undefined : e.target.value)}
              className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">Todas</option>
              {(ratesQuery.data?.items ?? []).map((n) => (
                <option key={n.nurseId} value={n.nurseId}>
                  {n.nurseNombre ?? n.nurseId}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wider text-text-muted">Desde</Label>
          <input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wider text-text-muted">Hasta</Label>
          <input
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      <div className="rounded-md border border-border overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-surface-alt text-[10px] uppercase tracking-wider text-text-muted">
            <tr>
              <th className="px-3 py-2 text-left">Fecha</th>
              {isAdmin && <th className="px-3 py-2 text-left">Enfermera</th>}
              <th className="px-3 py-2 text-left">Entrada</th>
              <th className="px-3 py-2 text-left">Salida</th>
              <th className="px-3 py-2 text-left">Duración</th>
              {canDelete && <th className="px-3 py-2 text-right">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {shifts.isLoading && (
              <tr>
                <td colSpan={colCount} className="px-3 py-6 text-center text-text-muted">
                  Cargando…
                </td>
              </tr>
            )}
            {shifts.data?.items.length === 0 && (
              <tr>
                <td colSpan={colCount} className="px-3 py-6 text-center text-text-muted">
                  Sin jornadas en el rango.
                </td>
              </tr>
            )}
            {(shifts.data?.items ?? []).map((s) => {
              const start = new Date(s.inicioAt);
              const end = s.finAt !== null ? new Date(s.finAt) : null;
              const dur =
                end === null
                  ? '—'
                  : (() => {
                      const ms = end.getTime() - start.getTime();
                      const h = Math.floor(ms / 3_600_000);
                      const m = Math.floor((ms % 3_600_000) / 60_000);
                      return `${h}h ${m}m`;
                    })();
              return (
                <tr key={s.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono text-xs">{s.fecha}</td>
                  {isAdmin && (
                    <td className="px-3 py-2">
                      {(s as { nurseNombre?: string }).nurseNombre ?? s.nurseId}
                    </td>
                  )}
                  <td className="px-3 py-2 tabular-nums">{colombiaHourMinute(start)}</td>
                  <td className="px-3 py-2 tabular-nums">
                    {end !== null ? (
                      colombiaHourMinute(end)
                    ) : (
                      <Badge variant="amber">
                        <Clock className="w-3 h-3" /> en curso
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums">{dur}</td>
                  {canDelete && (
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            setEditing({
                              shift: s,
                              nurse: (s as { nurseNombre?: string }).nurseNombre ?? s.nurseId,
                            })
                          }
                          title="Editar horas · cerrar o reabrir esta jornada"
                        >
                          <Pencil className="w-3.5 h-3.5 text-primary" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            setToDelete({
                              id: s.id,
                              fecha: s.fecha,
                              nurse: (s as { nurseNombre?: string }).nurseNombre ?? s.nurseId,
                            })
                          }
                          title="Eliminar esta jornada (corrige una entrada marcada por error)"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-danger" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>

      <ShiftEditDialog
        editing={editing}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => {
          if (!o) setToDelete(null);
        }}
        danger
        loading={deleteShift.isPending}
        title="Eliminar jornada"
        description={
          toDelete !== null
            ? `Vas a eliminar la jornada de ${toDelete.nurse} del ${toDelete.fecha}. Esta entrada dejará de contar en la liquidación y no se puede deshacer.`
            : undefined
        }
        confirmLabel="Eliminar"
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Formulario de registro manual de jornada (admin)                            */
/* -------------------------------------------------------------------------- */

function ManualShiftForm({ nurses }: { nurses: NurseRates[] }): JSX.Element {
  const { showToast } = useToast();
  const createShift = useCreateShift();
  const hoy = colombiaDateString(new Date());
  const [open, setOpen] = useState<boolean>(false);
  const [nurseId, setNurseId] = useState<string>('');
  const [fecha, setFecha] = useState<string>(hoy);
  const [horaInicio, setHoraInicio] = useState<string>('07:00');
  const [horaFin, setHoraFin] = useState<string>('');

  const reset = (): void => {
    setNurseId('');
    setFecha(hoy);
    setHoraInicio('07:00');
    setHoraFin('');
  };

  const submit = (): void => {
    if (nurseId === '') {
      showToast({ kind: 'danger', title: 'Selecciona la enfermera' });
      return;
    }
    if (horaFin !== '' && horaFin <= horaInicio) {
      showToast({ kind: 'danger', title: 'La salida debe ser posterior a la entrada' });
      return;
    }
    createShift.mutate(
      {
        nurseId,
        fecha,
        horaInicio,
        horaFin: horaFin === '' ? null : horaFin,
      },
      {
        onSuccess: () => {
          showToast({ kind: 'success', title: 'Jornada registrada' });
          reset();
          setOpen(false);
        },
        onError: (err) =>
          showToast({ kind: 'danger', title: 'No se pudo registrar', description: err.message }),
      },
    );
  };

  if (!open) {
    return (
      <div className="mb-4">
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
          <Plus className="w-4 h-4" /> Registrar jornada manual
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-md border border-primary/30 bg-primary-soft/40 p-4">
      <div className="text-[11px] uppercase tracking-wider font-bold text-primary mb-1">
        Registrar jornada manual
      </div>
      <p className="text-xs text-text-muted mb-3">
        Para un día que la enfermera olvidó marcar (incluso anterior). Deja la salida vacía si
        aún no ha terminado la jornada. Las horas son hora Colombia.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1 min-w-[200px]">
          <Label className="text-[11px] uppercase tracking-wider text-text-muted">Enfermera</Label>
          <select
            value={nurseId}
            onChange={(e) => setNurseId(e.target.value)}
            className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">— Selecciona —</option>
            {nurses.map((n) => (
              <option key={n.nurseId} value={n.nurseId}>
                {n.nurseNombre ?? n.nurseId}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wider text-text-muted">Fecha</Label>
          <input
            type="date"
            value={fecha}
            max={hoy}
            onChange={(e) => setFecha(e.target.value)}
            className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wider text-text-muted">Entrada</Label>
          <input
            type="time"
            value={horaInicio}
            onChange={(e) => setHoraInicio(e.target.value)}
            className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wider text-text-muted">
            Salida <span className="normal-case text-text-muted/70">(opcional)</span>
          </Label>
          <input
            type="time"
            value={horaFin}
            onChange={(e) => setHoraFin(e.target.value)}
            className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={submit} disabled={createShift.isPending}>
            {createShift.isPending ? 'Registrando…' : 'Registrar'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              reset();
              setOpen(false);
            }}
            disabled={createShift.isPending}
          >
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Diálogo de edición de jornada (admin): cerrar · reabrir · corregir horas    */
/* -------------------------------------------------------------------------- */

function ShiftEditDialog({
  editing,
  onClose,
}: {
  editing: { shift: NurseShift; nurse: string } | null;
  onClose: () => void;
}): JSX.Element {
  const { showToast } = useToast();
  const editShift = useEditShift();
  const [horaInicio, setHoraInicio] = useState<string>('');
  const [horaFin, setHoraFin] = useState<string>('');

  // Precargar los campos cada vez que se abre para una jornada distinta.
  useEffect(() => {
    if (editing === null) return;
    setHoraInicio(colombiaHourMinute(new Date(editing.shift.inicioAt)));
    setHoraFin(
      editing.shift.finAt !== null ? colombiaHourMinute(new Date(editing.shift.finAt)) : '',
    );
  }, [editing]);

  const save = (): void => {
    if (editing === null) return;
    if (horaFin !== '' && horaFin <= horaInicio) {
      showToast({ kind: 'danger', title: 'La salida debe ser posterior a la entrada' });
      return;
    }
    editShift.mutate(
      {
        id: editing.shift.id,
        body: {
          horaInicio,
          horaFin: horaFin === '' ? null : horaFin,
        },
      },
      {
        onSuccess: () => {
          showToast({ kind: 'success', title: 'Jornada actualizada' });
          onClose();
        },
        onError: (err) =>
          showToast({ kind: 'danger', title: 'No se pudo actualizar', description: err.message }),
      },
    );
  };

  return (
    <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar jornada</DialogTitle>
          <DialogDescription>
            {editing !== null
              ? `${editing.nurse} · ${editing.shift.fecha}. Deja la salida vacía para reabrir la jornada (sin salida registrada).`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] uppercase tracking-wider text-text-muted">Entrada</Label>
            <input
              type="time"
              value={horaInicio}
              onChange={(e) => setHoraInicio(e.target.value)}
              className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] uppercase tracking-wider text-text-muted">
              Salida <span className="normal-case text-text-muted/70">(vacía = abierta)</span>
            </Label>
            <input
              type="time"
              value={horaFin}
              onChange={(e) => setHoraFin(e.target.value)}
              className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={editShift.isPending}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={editShift.isPending}>
            {editShift.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* Tabla de tarifas                                                            */
/* -------------------------------------------------------------------------- */

function RatesTable(): JSX.Element {
  const ratesQuery = useNurseRates();
  return (
    <Card className="p-6">
      <CardHeader className="px-0 pt-0 pb-4 mb-4 border-b border-border">
        <CardTitle>Tarifas oficiales · COP</CardTitle>
        <CardDescription>
          Valores aplicados al calcular cada jornada. Cambiarlas desde Configuración.
        </CardDescription>
      </CardHeader>
      <div className="rounded-md border border-border overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-surface-alt text-[10px] uppercase tracking-wider text-text-muted">
            <tr>
              <th className="px-3 py-2 text-left">Enfermera</th>
              <th className="px-3 py-2 text-right">Jornada normal</th>
              <th className="px-3 py-2 text-right">Bono &gt;20 pacientes</th>
              <th className="px-3 py-2 text-right">Bono hora extra ≥ 2pm</th>
            </tr>
          </thead>
          <tbody>
            {(ratesQuery.data?.items ?? []).map((r: NurseRates) => (
              <tr key={r.nurseId} className="border-t border-border">
                <td className="px-3 py-2 font-semibold">{r.nurseNombre ?? r.nurseId}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatCop(r.tarifaJornadaNormal)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatCop(r.tarifaAdicionalMas20Pacientes)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatCop(r.tarifaHoraExtraDespues2pm)}
                </td>
              </tr>
            ))}
            {ratesQuery.data?.items.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-text-muted">
                  Sin tarifas cargadas. Ejecuta el seed o créalas en Configuración.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Utils                                                                       */
/* -------------------------------------------------------------------------- */

function formatCop(value: number): string {
  return value.toLocaleString('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  });
}

export default PayrollPage;
